import { useClientOptions } from '../providers/ClientOptionsProvider'
import { useUser } from '../providers/UserProvider'

/**
 * The company the current surface belongs to, for upload paths that must attribute a file.
 *
 * Precedence is the logged-in user first, then the options provider's `clientId`. Both sources are
 * needed because not every app mounts both providers:
 *
 * - `apps/app` and `apps/painter-view` mount `UserProvider`, so `user.app_metadata.companyId` wins.
 * - `apps/customer-view` mounts NO `UserProvider`. `useUser()` returns the empty `UserContext`
 *   default, so `companyId` was `undefined` there. It does mount `ClientOptionsProvider` with
 *   `clientId={companyId}` from the `viewQuote` query, which is why the fallback resolves.
 *
 * Why this matters (#22526): `cloudinaryUploadFile` sends `folder: companyId`, and `FormData.append`
 * coerces with `String()`. An `undefined` companyId therefore wrote a Cloudinary folder literally
 * named `undefined` — 13,792 such references in production, each an asset whose owning company
 * nothing records. `uploadFile` now refuses a falsy companyId outright, so this hook is what keeps
 * the customer signature path working rather than failing.
 *
 * Returns `undefined` when neither source has an id. Callers must not paper over that with a
 * placeholder — an unattributable upload is worse than a refused one.
 */
export function useCompanyId(): string | undefined {
  const { user } = useUser()
  const { clientId } = useClientOptions()

  return user?.app_metadata?.companyId || clientId || undefined
}

export default useCompanyId
